import type { UiConfig } from '@/types/ui';

// Frontend-consumed subset of sound_anomalies.config.settings.FrontendUiConfig.
export const DEFAULT_UI_CONFIG: UiConfig = {
  debug_logging_enabled: false,
  condition_history: {
    default_view_days: 365,
    max_query_window_days: 730,
  },
  websocket: {
    reconnect_initial_delay_seconds: 1,
    reconnect_backoff_factor: 2,
    max_reconnect_attempts: 5,
    cleanup_delay_seconds: 1.0,
  },
  device_control: {
    connect_timeout_seconds: 10,
    command_ack_timeout_seconds: 5,
    command_result_timeout_seconds: 30,
  },
  training: {
    dataset_stale_seconds: 300,
    jobs_poll_interval_seconds: 10,
    jobs_stale_seconds: 5,
    queue_poll_interval_seconds: 10,
    queue_stale_seconds: 5,
    summary_poll_interval_seconds: 10,
    summary_stale_seconds: 5,
  },
  optimization: {
    results_poll_interval_seconds: 10,
    results_stale_seconds: 1,
    strategies: {
      fast: {
        trials_per_model: 10,
        estimated_minutes_per_model: 5,
        description: 'Quick optimization with fewer trials',
      },
      balanced: {
        trials_per_model: 20,
        estimated_minutes_per_model: 10,
        description: 'Good balance between speed and quality',
      },
      thorough: {
        trials_per_model: 50,
        estimated_minutes_per_model: 25,
        description: 'Comprehensive search for best parameters',
      },
    },
  },
  capture: {
    sessions_poll_interval_seconds: 5,
    elapsed_refresh_interval_seconds: 1,
  },
  monitoring: {
    device_poll_interval_seconds: 30,
    device_stale_seconds: 15,
    live_stats_poll_interval_seconds: 10,
    per_model_flash_seconds: 1,
    streaming_activity_check_interval_seconds: 1,
    streaming_activity_idle_seconds: 10,
    copy_feedback_seconds: 1.5,
    detection_poll_interval_seconds: 5,
    alarm_range_presets_hours: [24, 72, 168],
    recent_activity_window_hours: 24,
    detection_stale_seconds: 0,
    rotation_limit_default: 15,
    job_limit_default: 10,
    calibration_limit_default: 10,
    drift_limit_default: 10,
    org_stale_seconds: 60,
    model_state_poll_interval_seconds: 30,
    model_state_stale_seconds: 15,
    model_comparison_hours_default: 6,
    model_comparison_hours_max: 168,
    model_comparison_bucket_minutes_default: 5,
    model_comparison_bucket_minutes_min: 1,
    model_comparison_bucket_minutes_max: 240,
    model_comparison_max_buckets: 2000,
    auto_capture_poll_interval_seconds: 15,
    auto_capture_stale_seconds: 5,
    audio_mirror: {
      visualization_max_buffered_chunks: 1,
    },
    spectrogram: {
      fft_bins: 512,
      max_fps: 12,
      height_px: 64,
      min_db: -80,
      max_db: 0,
    },
    device_list_poll_interval_seconds: 5,
    device_list_stale_seconds: 3,
    device_list_page_size_default: 50,
    device_list_large_threshold: 200,
    device_list_poll_interval_seconds_large: 20,
    fleet_poll_interval_seconds: 60,
    fleet_stale_seconds: 30,
  },
  detect: {
    devices_poll_interval_seconds: 10,
    devices_stale_seconds: 5,
    device_list_page_size_default: 200,
    fleet_poll_interval_seconds: 60,
    fleet_stale_seconds: 30,
    jobs_poll_interval_seconds: 5,
    jobs_stale_seconds: 1,
  },
  reports: {
    poll_interval_seconds: 30,
    stale_seconds: 20,
    templates_stale_seconds: 300,
  },
  models: {
    poll_interval_seconds: 30,
    stale_seconds: 20,
    quick_test_status_delay_seconds: 1,
  },
  datasets: {
    poll_interval_seconds: 30,
    stale_seconds: 20,
  },
  software: {
    device_stale_seconds: 300,
    fleet_poll_interval_seconds: 300,
    fleet_stale_seconds: 120,
    outdated_stale_seconds: 120,
  },
  device_config: {
    config_stale_seconds: 300,
    defaults_stale_seconds: 600,
    status_poll_interval_seconds: 30,
    status_stale_seconds: 30,
  },
  health_guard: {
    poll_interval_seconds: 30,
    stale_seconds: 15,
  },
  clips: {
    poll_interval_seconds: 30,
    stale_seconds: 20,
    device_limit_default: 10,
    fleet_alerts_limit_default: 15,
    detect_events_limit_default: 40,
  },
  fleet_management: {
    device_selector_page_size_default: 200,
    device_selector_stale_seconds: 30,
    focus_delay_seconds: 0,
  },
  fleet_overview: {
    poll_interval_seconds: 60,
    stale_seconds: 30,
  },
  toast: {
    brief_seconds: 3,
    short_seconds: 5,
    medium_seconds: 7,
    long_seconds: 10,
  },
};

export const resolveUiConfig = (
  config?: UiConfig | null,
  allowFallback: boolean = false
): UiConfig => {
  if (config) {
    return config;
  }
  if (!allowFallback) {
    throw new Error('ui_config_missing');
  }
  return DEFAULT_UI_CONFIG;
};
